"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { useSession } from "next-auth/react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { BdCard, BdWallet2 } from "solar-icon-react/bd";
import { cn, formatNumber, formatNumberWithDots } from "@/lib/utils";
import { IDefaultPackage, IPackages, ISelectableBaseProps } from "@/interfaces";
import { useSettings } from "@/hooks/useSettings";
import { CustomButton, ErrorMessage } from "../ui";
import styles from "./paymentCheckout.module.css";

interface Props {
  onClose: () => void;
  onSubmit: (data: {
    digit: string;
    packageId?: string;
    bank?: string;
    topup?: boolean;
  }) => void;
  walletBalance?: string;
  inquiryPrice?: string | number;
  error?: Error | null;
  isLoading: boolean;
  incrementBtnId?: string;
  selectedPackage?: IDefaultPackage | IPackages;
  hasPackage?: boolean;
  showManualError?: string | null;
}

const basePrices: ISelectableBaseProps[] = [
  { label: "50000", isSelected: false },
  { label: "80000", isSelected: true },
  { label: "200000", isSelected: false },
];

// This form is also used to start a direct service payment. The 50,000 toman
// minimum therefore belongs to the wallet top-up state, not the shared schema.
const checkoutPaymentSchema = z.object({
  bank: z.string().optional(),
  digit: z.string().refine((value) => /^\d+$/.test(value), {
    message: "لطفاً مبلغ را به‌صورت عدد وارد کنید.",
  }),
});

export default function InsufficientBalanceModal({
  onSubmit,
  walletBalance,
  inquiryPrice,
  error,
  isLoading,
  incrementBtnId,
  selectedPackage,
  showManualError = null,
}: Props) {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors: formError },
  } = useForm<z.infer<typeof checkoutPaymentSchema>>({
    resolver: zodResolver(checkoutPaymentSchema),
    defaultValues: { digit: "50000", bank: "" },
    mode: "onChange",
  });
  const { data: session } = useSession();
  const { gateways: availableGateways, hasActiveGateways } = useSettings(
    session?.user.token,
  );
  const inputValue = watch("digit");
  const bankValue = watch("bank");
  const inputRef = useRef<HTMLInputElement>(null);
  const inquiryAmount = Math.floor(Number(inquiryPrice || 0));
  const isSingleInquiry = selectedPackage?.id === "p0";
  const selectedPackageAmount = Number(
    selectedPackage?.price?.replace(/[^\d]/g, "") || 0,
  );
  const originalPackageAmount =
    selectedPackage && "inquiryPrice" in selectedPackage
      ? Number(selectedPackage.inquiryPrice || selectedPackageAmount)
      : selectedPackageAmount;
  const originalAmount = isSingleInquiry
    ? inquiryAmount
    : originalPackageAmount;
  const packageDiscount = isSingleInquiry
    ? 0
    : Math.max(0, originalPackageAmount - selectedPackageAmount);
  const payableAmount = isSingleInquiry ? inquiryAmount : selectedPackageAmount;
  const selectedServiceName = selectedPackage
    ? "name" in selectedPackage
      ? selectedPackage.name
      : selectedPackage.title
    : "استعلام";
  const [prices, setPrices] = useState<ISelectableBaseProps[]>(basePrices);
  const [payments, setPayments] = useState<ISelectableBaseProps[]>([]);
  const [isTopupOpen, setIsTopupOpen] = useState(false);
  const [isCustomTopup, setIsCustomTopup] = useState(false);

  useEffect(() => {
    if (!inputValue) return;
    const isPreset = basePrices.some((item) => item.label === inputValue);
    setPrices((current) =>
      current.map((item) => ({
        ...item,
        isSelected: isPreset && item.label === inputValue,
      })),
    );
  }, [inputValue]);

  useEffect(() => {
    if (!availableGateways.length) return;
    const defaultBank =
      availableGateways.find((item) => item.isSelected)?.key ||
      availableGateways[0].key;
    setPayments(
      availableGateways.map((item) => ({
        ...item,
        isSelected: item.key === defaultBank,
      })),
    );
    setValue("bank", defaultBank, { shouldValidate: false });
  }, [availableGateways, setValue]);

  const selectAmount = (item: ISelectableBaseProps) => {
    setValue("digit", item.label, { shouldValidate: false });
    clearErrors("digit");
  };

  const selectGateway = (index: number) => {
    const gateway = payments[index];
    setValue("bank", gateway.key, { shouldValidate: false });
    setPayments((current) =>
      current.map((item, itemIndex) => ({
        ...item,
        isSelected: itemIndex === index,
      })),
    );
  };

  const submit = () => {
    if (isTopupOpen && !validateTopupAmount(inputValue)) {
      return;
    }

    handleSubmit((data) => {
      onSubmit({
        ...data,
        digit: isTopupOpen ? data.digit : String(payableAmount),
        bank: bankValue,
        packageId: "",
        topup: isTopupOpen,
      });
    })();
  };

  const validateTopupAmount = (amount: string) => {
    if (Number(amount) < 50000) {
      setError("digit", {
        type: "min",
        message: "حداقل مبلغ افزایش اعتبار کیف پول ۵۰٬۰۰۰ تومان است.",
      });
      return false;
    }

    clearErrors("digit");
    return true;
  };

  return (
    <>
      <div className={styles.insufficientContent}>
        <div className={styles.summaryTop}>
          <h3>خلاصه سفارش</h3>
          <span>همه مبالغ به تومان</span>
        </div>
        <div className={styles.receipt}>
          <div className={styles.receiptTitle}>
            <span className={styles.receiptIcon}>
              <BdWallet2 size={21} />
            </span>
            <div>
              <strong>{selectedServiceName}</strong>
              <small>۱ استعلام · بدون محدودیت بسته</small>
            </div>
          </div>
          <div className={styles.receiptRow}>
            <span>مبلغ اولیه</span>
            <b>{formatNumber(originalAmount)} تومان</b>
          </div>
          <div className={styles.receiptRow}>
            <span>تخفیف بسته</span>
            <b>{formatNumber(packageDiscount)} تومان</b>
          </div>
          <div className={styles.receiptTotal}>
            <strong>مبلغ قابل پرداخت</strong>
            <span className={styles.receiptAmount}>
              {formatNumber(payableAmount)}
              <small className={styles.currency}>تومان</small>
            </span>
          </div>
        </div>

        <div className={styles.balanceCard}>
          <div className={styles.balanceMain}>
            <BdWallet2 size={18} />
            <span>موجودی کیف پول</span>
            <b>{formatNumber(Number(walletBalance || 0))} تومان</b>
          </div>
          <button
            className={styles.topupLink}
            type="button"
            onClick={() => {
              setIsTopupOpen((current) => !current);
              clearErrors("digit");
            }}
          >
            {isTopupOpen ? "− بستن شارژ" : "+ افزایش اعتبار"}
          </button>
        </div>

        {isTopupOpen && (
          <div className={styles.topup}>
            <div className={styles.paymentHeading} style={{ marginTop: 0 }}>
              <h3>افزایش اعتبار کیف پول</h3>
            </div>
            <p className={styles.topupDescription}>
              مبلغ شارژ به موجودی شما اضافه می‌شود.
            </p>
            <div className={styles.amountChoices}>
              {prices.map((item) => (
                <button
                  key={item.label}
                  className={cn(
                    "rounded-[9px] border px-2 py-2 text-[11px] font-extrabold transition-colors",
                    item.isSelected
                      ? "border-[#5a4bd2] bg-[#efedff] text-[#5a4bd2]"
                      : "border-[#e5e4ef] bg-[#fafaff] text-[#3b3b56]",
                  )}
                  type="button"
                  onClick={() => {
                    setIsCustomTopup(false);
                    selectAmount(item);
                  }}
                >
                  {formatNumberWithDots(item.label)}
                </button>
              ))}
              <button
                className={cn(
                  "rounded-[9px] border px-2 py-2 text-[11px] font-extrabold transition-colors",
                  isCustomTopup
                    ? "border-[#5a4bd2] bg-[#5b4bd7] text-white"
                    : "border-[#e5e4ef] bg-[#fafaff] text-[#3b3b56]",
                )}
                type="button"
                onClick={() => setIsCustomTopup(true)}
              >
                دلخواه
              </button>
            </div>
            {isCustomTopup && (
              <Controller
                name="digit"
                control={control}
                render={({ field }) => (
                  <div className={styles.customAmount}>
                    <label htmlFor="wallet-topup-amount">
                      مبلغ دلخواه (تومان)
                    </label>
                    <input
                      {...field}
                      id="wallet-topup-amount"
                      ref={inputRef}
                      maxLength={9}
                      inputMode="numeric"
                      onChange={(event) => {
                        const amount = event.target.value.replace(/\D/g, "");
                        field.onChange(amount);

                        if (isTopupOpen) validateTopupAmount(amount);
                      }}
                      placeholder="۵۰٬۰۰۰"
                    />
                  </div>
                )}
              />
            )}
            {formError.digit?.message && (
              <p className="mt-2 text-xs text-red-600">
                {formError.digit.message}
              </p>
            )}
          </div>
        )}

        <div className={styles.gatewaySection}>
          <div className={styles.paymentHeading}>
            <h3>انتخاب درگاه</h3>
          </div>
          {hasActiveGateways ? (
            <div className={styles.methods}>
              {payments.map((gateway, index) => (
                <button
                  key={gateway.key || index}
                  className={cn(
                    styles.method,
                    gateway.isSelected && styles.methodSelected,
                  )}
                  type="button"
                  onClick={() => selectGateway(index)}
                >
                  <span className={styles.methodTop}>
                    {gateway.icon ? (
                      <Image
                        src={gateway.icon}
                        alt={gateway.label}
                        width={22}
                        height={22}
                      />
                    ) : (
                      <BdCard size={22} />
                    )}
                    <span className={styles.methodRadio} />
                  </span>
                  <span className={styles.methodInfo}>
                    <strong>{gateway.label}</strong>
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <ErrorMessage message="هیچ درگاه پرداخت فعالی در دسترس نیست" />
          )}
        </div>

        <div className={styles.paymentMethodSection}>
          <div className={styles.paymentHeading}>
            <h3>روش پرداخت</h3>
          </div>
          {hasActiveGateways ? (
            <div className={styles.methods}>
              {payments.slice(0, 1).map((gateway, index) => (
                <button
                  key={gateway.key || index}
                  className={cn(styles.method, styles.methodSelected)}
                  type="button"
                  onClick={() => selectGateway(index)}
                >
                  <span className={styles.methodTop}>
                    <BdCard size={22} />
                    <span className={styles.methodRadio} />
                  </span>
                  <span className={styles.methodInfo}>
                    <strong>پرداخت مستقیم</strong>
                    <small>با کارت بانکی</small>
                  </span>
                </button>
              ))}
              <button
                className={cn(styles.method, styles.methodDisabled)}
                disabled
              >
                <span className={styles.methodTop}>
                  <BdWallet2 size={22} />
                  <span className={styles.methodRadio} />
                </span>
                <span className={styles.methodInfo}>
                  <strong>کیف پول</strong>
                  <small>موجودی کافی نیست</small>
                </span>
              </button>
            </div>
          ) : (
            <ErrorMessage message="هیچ درگاه پرداخت فعالی در دسترس نیست" />
          )}
        </div>
        {(showManualError || error) && (
          <div className="mt-4">
            <ErrorMessage message={showManualError || error?.message} />
          </div>
        )}
      </div>
      <div className={styles.actionArea}>
        <CustomButton
          id={`wallet-${incrementBtnId}`}
          isLoading={isLoading}
          disabled={!hasActiveGateways}
          className={styles.primaryButton}
          onClick={submit}
        >
          پرداخت{" "}
          {formatNumber(isTopupOpen ? Number(inputValue || 0) : payableAmount)}{" "}
          تومان
        </CustomButton>
        <p className={styles.actionNote}>
          بعد از شارژ، می‌توانید سفارش را از کیف پول پرداخت کنید.
        </p>
      </div>
    </>
  );
}
